import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import propTypes from "prop-types";

import FullScreenDialog from "./MaterialComponents/FullScreenDialog";
import SureYouWantToCancel from "./MaterialComponents/SureYouWantToCancel";

const PREFIX = "FormStepper";

const classes = {
  root: `${PREFIX}-root`,
  layout: `${PREFIX}-layout`,
  socialPageSetupLayout: `${PREFIX}-socialPageSetupLayout`,
  buttons: `${PREFIX}-buttons`,
  button: `${PREFIX}-button`,
  title: `${PREFIX}-title`,
};

const FormStepperWrapper = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    flexDirection: "column",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    overflow: "hidden", 
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },


  [`& .${classes.layout}`]: {
    width: "100%", 
    maxWidth: "800px", 
    margin: "0 auto", 
    boxSizing: "border-box", 
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  
  [`& .${classes.socialPageSetupLayout}`]: {
    width: "100%", 
    maxWidth: "1000px", 
    margin: "0 auto",
    boxSizing: "border-box",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },


  [`& .${classes.buttons}`]: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    alignItems: "center",
    // gap: theme.spacing(1), 
  },

  [`& .${classes.button}`]: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.title}`]: {
    marginTop: "24px",
    fontSize: "1.8rem",
  },
}));

export function FormStepper({
  children,
  stepImperatively,
  setStepImperatively,
  onSubmit,
  initialValues,
}) {
  const childrenArray = React.Children.toArray(children);
  const [step, setStep] = useState(0);
  const currentChild = childrenArray[step];
  const [penultimateSubmission, setPenultimateSubmission] = useState(false);

  const isLastStep = () => {
    return step === childrenArray.length - 1;
  };

  const isPenultimateStep = () => {
    return step === childrenArray.length - 2;
  };

  useEffect(() => {
    try {
      window.scroll({
        top: 0,
        left: 0,
      });
    } catch (error) {
      window.scrollTo(0, 0);
    }
  }, [step]);

  useEffect(() => {
    if (step !== 0 && stepImperatively !== null) {
      setStep(stepImperatively);
    }
    if (stepImperatively !== null) {
      setStepImperatively(null);
    }
  }, [stepImperatively]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={currentChild.props.validationSchema}
      onSubmit={async (values, helpers) => {
        if (penultimateSubmission || isLastStep()) {
          await onSubmit(values, helpers);
        } else {
          setStep((s) => s + 1);
          helpers.setTouched({});
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => (
        <FormStepperWrapper>
        <Form autoComplete="off">
          <Box
            className={
              currentChild.props.label === "Social Page Setup"
                ? classes.socialPageSetupLayout
                : classes.layout
            }
          >
            <Paper elevation={2} className={classes.root}>
              <Typography
                className={classes.title}
                align="center"
                color="primary"
                variant="h5"
                // component={"span"}
              >
                {childrenArray[step].props.title}
              </Typography>
              <Stepper alternativeLabel activeStep={step}>
                {childrenArray.map((child, index) => (
                  <Step key={child.props.label} completed={step > index}>
                    <StepLabel>{child.props.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {currentChild}
 <div className={classes.buttons}>
  {/* Left side */}
  <div style={{ flex: 1 }}>
    {step > 0 && (
      <Button
  className={`button-secondary ${classes.button}`}
  disabled={isSubmitting}
  onClick={() => setStep((s) => s - 1)}
>
  Back
</Button>

    )}
  </div>

  {/*Center advanced set button*/}
  <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
    <FullScreenDialog icon={false} />
  </div>

  {/* Right side */}
  <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 8 }}>
    <SureYouWantToCancel />
    {isPenultimateStep() && (
       <Button
    className={`button-primary ${classes.button}`}
    startIcon={
      isSubmitting && penultimateSubmission ? (
        <CircularProgress size="1rem" />
      ) : null
    }
    onClick={() => {
      setPenultimateSubmission(true);
      handleSubmit();
    }}
    disabled={isSubmitting}
    type="submit"
  >
    Create
  </Button>
    )}
<Button
  className={`button-primary ${classes.button}`}
  startIcon={
    isSubmitting && !penultimateSubmission ? (
      <CircularProgress size="1rem" />
    ) : null
  }
  disabled={isSubmitting}
  type="submit"
>
  {isSubmitting
    ? "Submitting"
    : isLastStep()
    ? "Create"
    : isPenultimateStep()
    ? "Review"
    : "Next"}
</Button>

  </div>
</div>



            </Paper>
          </Box>
        </Form>
        </FormStepperWrapper>
      )}
    </Formik>
  );
}

FormStepper.propTypes = {
  stepImperatively: propTypes.number,
  setStepImperatively: propTypes.func,
  onSubmit: propTypes.func,
  children: propTypes.arrayOf(propTypes.element),
  initialValues: propTypes.object,
};
